<script setup lang="ts">
import { cn } from "@/lib/utils";
import { toneChipStyle, toneSurfaceStyle, type LayerTone } from "./layerTone";

const props = withDefaults(defineProps<{
  icon: string;
  title: string;
  badge: string;
  badgeTone: LayerTone;
  subtitle?: string;
  indent?: 0 | 1 | 2 | 3;
  dimmed?: boolean;
}>(), {
  subtitle: undefined,
  indent: 0,
  dimmed: false,
});

const indentWidth = computed(() => props.indent * 14);
const railLeft = computed(() => Math.max(indentWidth.value - 14, 0));
</script>

<template>
  <div class="relative">
    <div
      v-if="indent > 0"
      class="pointer-events-none absolute inset-y-0 left-0"
      :style="{ width: `${indentWidth}px` }"
    >
      <div
        class="absolute bottom-0 top-0 w-px bg-border"
        :style="{ left: `${railLeft}px` }"
      />
      <div
        class="absolute h-px w-[10px] bg-border"
        :style="{ left: `${railLeft}px`, top: '20px' }"
      />
    </div>

    <div
      :class="cn('rounded-xl border shadow-sm transition-opacity', dimmed ? 'opacity-45' : 'opacity-100')"
      :style="{
        ...toneSurfaceStyle(badgeTone, { dimmed }),
        marginLeft: `${indentWidth}px`,
      }"
    >
      <div class="flex items-start gap-3 border-b px-4 py-3">
        <div
          class="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border"
          :style="toneChipStyle(badgeTone)"
        >
          <Icon mode="svg" size="16" :name="icon" />
        </div>

        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="text-[13px] font-semibold text-foreground">{{ title }}</h3>
            <span
              class="inline-flex items-center rounded-full border px-1.5 py-0.5 text-[8.5px] font-bold tracking-[0.08em]"
              :style="toneChipStyle(badgeTone, { solid: badgeTone !== 'base' })"
            >
              {{ badge }}
            </span>
          </div>
          <p v-if="subtitle" class="mt-1 text-[11.5px] leading-snug text-muted-foreground">
            {{ subtitle }}
          </p>
        </div>

        <div class="shrink-0">
          <slot name="headerRight" />
        </div>
      </div>

      <div :class="cn('space-y-2 px-4 py-3', props.dimmed ? 'pointer-events-none' : '')">
        <slot />
      </div>
    </div>
  </div>
</template>
